import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/AuthContext";
import { useAuth } from "@/AuthContext";
import { useDropzone } from "react-dropzone";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, X, MagnifyingGlass, FileText, Trash, DownloadSimple,
  UploadSimple, CheckCircle, ArrowLeft, ArrowRight, Eye, PencilSimple, CaretRight, Storefront, Buildings
} from "@phosphor-icons/react";
import { DocumentViewerModal } from "../components/DocumentViewer";
import ExportButton from "../components/ExportButton";
import BulkDatesModal from "../components/BulkDatesModal";
import DocumentEditModal from "../components/DocumentEditModal";
import useCatalog from "../hooks/useCatalog";

const GENRES = ["Fiscal", "Laboral", "Operativa", "Legal", "Ambiental", "Administrativa"];
const YEARS = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i + 2);
const fmtDate = (v) => {
  if (!v) return "—";
  const [y, m, d] = String(v).slice(0, 10).split("-");
  return y && m && d ? `${d}/${m}/${y}` : v;
};


export default function Documents() {
  const { user, can } = useAuth();
  const authorities = useCatalog("autoridad");
  const [docs, setDocs] = useState([]);
  const [period, setPeriod] = useState("");
  const [authority, setAuthority] = useState("");
  const [genre, setGenre] = useState("");
  const [search, setSearch] = useState("");
  const [wizardOpen, setWizardOpen] = useState(false);
  const [viewDoc, setViewDoc] = useState(null);
  const [editDoc, setEditDoc] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const isAdmin = ["super_admin", "admin"].includes(user?.role);
  const isEditable = (d) => ["pendiente", "rechazado", undefined, null, ""].includes(d.approval_status) && (isAdmin || d.created_by === user?.id);
  const toggleSel = (id) => setSelected(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const canEdit = can("documents", "write");
  const canDelete = can("documents", "delete");

  const fetchDocs = useCallback(() => {
    const params = {};
    if (period) params.period = period;
    if (authority) params.authority = authority;
    if (genre) params.genre = genre;
    api.get("/documents", { params }).then(r => setDocs(r.data)).catch(()=>{});
  }, [period, authority, genre]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const filtered = docs.filter(d =>
    !search || d.name.toLowerCase().includes(search.toLowerCase()) ||
    d.filename?.toLowerCase().includes(search.toLowerCase())
  );

  // Agrupación Empresa › Autoridad (colapsado; la búsqueda despliega coincidencias)
  const [companies, setCompanies] = useState([]);
  const [openC, setOpenC] = useState({});
  const [openA, setOpenA] = useState({});
  useEffect(() => { api.get("/companies").then(r => setCompanies(r.data)).catch(() => {}); }, []);
  const groups = useMemo(() => {
    const names = Object.fromEntries(companies.map(c => [c.id, c.name]));
    const byC = {};
    filtered.forEach(d => {
      const cid = d.company_id || "__none__";
      const a = d.authority || "Sin autoridad";
      ((byC[cid] = byC[cid] || {})[a] = byC[cid][a] || []).push(d);
    });
    return Object.entries(byC).map(([id, auths]) => ({
      id, name: id === "__none__" ? "Sin empresa" : (names[id] || "Empresa"),
      total: Object.values(auths).reduce((s, l) => s + l.length, 0),
      auths: Object.entries(auths).sort(([a], [b]) => a.localeCompare(b)),
    })).sort((a, b) => a.name.localeCompare(b.name));
  }, [filtered, companies]);
  const forceOpen = !!search;
  const expandAll = () => {
    setOpenC(Object.fromEntries(groups.map(g => [g.id, true])));
    setOpenA(Object.fromEntries(groups.flatMap(g => g.auths.map(([a]) => [`${g.id}|${a}`, true]))));
  };
  const colCount = canEdit ? 12 : 11;
  const renderGroups = (renderRow) => groups.map(g => {
    const cOpen = forceOpen || !!openC[g.id];
    return (
      <React.Fragment key={g.id}>
        <tr className="bg-neutral-50 border-b border-neutral-200 cursor-pointer hover:bg-neutral-100" onClick={() => setOpenC(p => ({ ...p, [g.id]: !p[g.id] }))} data-testid={`docs-company-${g.id}`}>
          <td colSpan={colCount} className="px-5 py-3">
            <div className="flex items-center gap-3">
              <CaretRight size={14} weight="bold" className={`transition-transform duration-200 ${cOpen ? "rotate-90 text-blue-600" : "text-neutral-400"}`}/>
              <Storefront size={18} weight="duotone" className="text-blue-600"/>
              <span className="font-display font-bold text-[15px] text-neutral-900">{g.name}</span>
              <span className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">{g.total} documento(s) · {g.auths.length} autoridad(es)</span>
            </div>
          </td>
        </tr>
        {cOpen && g.auths.map(([a, list]) => {
          const k = `${g.id}|${a}`;
          const aOpen = forceOpen || !!openA[k];
          return (
            <React.Fragment key={k}>
              <tr className="border-b border-neutral-100 cursor-pointer hover:bg-neutral-50" onClick={() => setOpenA(p => ({ ...p, [k]: !p[k] }))} data-testid={`docs-auth-${g.id}-${a}`}>
                <td colSpan={colCount} className="pl-12 pr-5 py-2.5">
                  <div className="flex items-center gap-2.5">
                    <CaretRight size={12} weight="bold" className={`transition-transform duration-200 ${aOpen ? "rotate-90 text-blue-600" : "text-neutral-400"}`}/>
                    <Buildings size={15} weight="duotone" className="text-indigo-600"/>
                    <span className="font-bold text-sm">{a}</span>
                    <span className="ml-auto px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-700 text-xs font-bold tabular-nums">{list.length}</span>
                  </div>
                </td>
              </tr>
              {aOpen && list.map(renderRow)}
            </React.Fragment>
          );
        })}
      </React.Fragment>
    );
  });

  const onDelete = async (id) => {
    if (!window.confirm("¿Eliminar este documento?")) return;
    try {
      await api.delete(`/documents/${id}`);
      toast.success("Documento eliminado");
      fetchDocs();
    } catch (e) { toast.error("Error al eliminar"); }
  };

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-2">Módulo</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">Gestión de documentos</h1>
          <p className="text-neutral-500 mt-2">Filtra por período, autoridad y género. Sube nuevos archivos al asistente.</p>
        </div>
        {(canEdit || canDelete) && (
          <div className="flex gap-2">
            <ExportButton resource="documents"/>
            {canEdit && (
              <button onClick={()=>setWizardOpen(true)} data-testid="open-upload-wizard-btn"
                className="inline-flex items-center gap-2 px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-md transition-all">
                <Plus size={18} weight="bold" /> Nuevo documento
              </button>
            )}
          </div>
        )}
        {!canEdit && !canDelete && <ExportButton resource="documents"/>}
      </div>

      {/* Filters */}
      <div className="bg-white border border-neutral-200 rounded-md p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="relative">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" size={16} />
          <input value={search} onChange={(e)=>setSearch(e.target.value)} placeholder="Buscar por nombre..."
                 className="w-full pl-9 pr-3 py-2 border border-neutral-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                 data-testid="search-input"/>
        </div>
        <select value={period} onChange={(e)=>setPeriod(e.target.value)} className="border border-neutral-200 rounded-md px-3 py-2 text-sm bg-white" data-testid="filter-period">
          <option value="">Todos los periodos</option>
          {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <select value={authority} onChange={(e)=>setAuthority(e.target.value)} className="border border-neutral-200 rounded-md px-3 py-2 text-sm bg-white" data-testid="filter-authority">
          <option value="">Todas las autoridades</option>
          {authorities.map(a => <option key={a.id} value={a.name}>{a.name}</option>)}
        </select>
        <select value={genre} onChange={(e)=>setGenre(e.target.value)} className="border border-neutral-200 rounded-md px-3 py-2 text-sm bg-white" data-testid="filter-genre">
          <option value="">Todos los géneros</option>
          {GENRES.map(g => <option key={g} value={g}>{g}</option>)}
        </select>
      </div>

      {/* Table */}
      {canEdit && selected.size > 0 && (
        <div className="flex items-center justify-between gap-3 bg-blue-50 border border-blue-200 rounded-md px-4 py-2.5 text-sm" data-testid="bulk-toolbar">
          <span className="font-semibold text-blue-800" data-testid="bulk-selected-count">{selected.size} documento(s) seleccionados</span>
          <div className="flex gap-2">
            <button onClick={() => setSelected(new Set())} className="px-3 py-1.5 text-xs font-semibold text-neutral-600 hover:text-neutral-900" data-testid="bulk-clear-btn">Limpiar selección</button>
            <button onClick={() => setBulkOpen(true)} className="px-3 py-1.5 text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-md" data-testid="bulk-edit-dates-btn">Editar fechas ({selected.size})</button>
          </div>
        </div>
      )}
      {bulkOpen && <BulkDatesModal ids={[...selected]} onClose={() => setBulkOpen(false)}
        onDone={(close) => { fetchDocs(); if (close) { setBulkOpen(false); setSelected(new Set()); } }}/>}
      <div className="bg-white border border-neutral-200 rounded-md overflow-hidden">
        <div className="flex items-center justify-between text-xs text-neutral-500 px-5 py-2.5 border-b border-neutral-200 bg-neutral-50/50">
          <span data-testid="documents-summary">{groups.length} empresa(s) · {filtered.length} documento(s)</span>
          <div className="flex gap-3">
            <button onClick={expandAll} className="hover:text-blue-600 font-semibold" data-testid="docs-expand-all-btn">Expandir todo</button>
            <span className="text-neutral-300">·</span>
            <button onClick={() => { setOpenC({}); setOpenA({}); }} className="hover:text-blue-600 font-semibold" data-testid="docs-collapse-all-btn">Colapsar todo</button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1400px] table-fixed text-sm" data-testid="documents-table">
            <colgroup>
              {canEdit && <col className="w-10"/>}
              <col className="w-[22%]"/><col className="w-[6%]"/><col className="w-[7%]"/><col className="w-[8%]"/><col className="w-[8%]"/><col className="w-[8%]"/>
              <col className="w-[7%]"/><col className="w-[8%]"/><col className="w-[7%]"/><col className="w-[7%]"/><col className="w-[12%]"/>
            </colgroup>
            <thead>
              <tr className="border-b border-neutral-200 text-left text-xs uppercase tracking-widest text-neutral-500">
                {canEdit && (
                  <th className="pl-5 pr-1 py-3 w-8">
                    <input type="checkbox" className="w-4 h-4 accent-blue-600 cursor-pointer" title="Seleccionar editables" data-testid="bulk-select-all"
                      checked={filtered.some(isEditable) && filtered.filter(isEditable).every(d => selected.has(d.id))}
                      onChange={(e) => setSelected(e.target.checked ? new Set(filtered.filter(isEditable).map(d => d.id)) : new Set())}/>
                  </th>
                )}
                <th className="px-5 py-3 font-semibold">Documento</th>
                <th className="px-5 py-3 font-semibold">Periodo</th>
                <th className="px-5 py-3 font-semibold">Autoridad</th>
                <th className="px-5 py-3 font-semibold whitespace-nowrap">Emisión</th>
                <th className="px-5 py-3 font-semibold whitespace-nowrap">Vigencia</th>
                <th className="px-5 py-3 font-semibold whitespace-nowrap">Vencimiento</th>
                <th className="px-5 py-3 font-semibold">Género</th>
                <th className="px-5 py-3 font-semibold">Estatus</th>
                <th className="px-5 py-3 font-semibold">Aprobación</th>
                <th className="px-5 py-3 font-semibold">Creado</th>
                <th className="px-5 py-3 font-semibold text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {renderGroups(d => (
                <tr key={d.id} className={`border-b border-neutral-100 hover:bg-neutral-50 transition-colors ${selected.has(d.id) ? "bg-blue-50/60" : ""}`} data-testid={`doc-row-${d.id}`}>
                  {canEdit && (
                    <td className="pl-5 pr-1 py-3">
                      <input type="checkbox" className="w-4 h-4 accent-blue-600 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                        disabled={!isEditable(d)} title={isEditable(d) ? "Seleccionar" : "Solo pendientes o rechazados de los que eres responsable"}
                        checked={selected.has(d.id)} onChange={() => toggleSel(d.id)} data-testid={`doc-select-${d.id}`}/>
                    </td>
                  )}
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-blue-50 text-blue-700 rounded-md flex items-center justify-center border border-blue-100">
                        <FileText size={16} weight="duotone" />
                      </div>
                      <div className="min-w-0">
                        <Link to={`/documentos/${d.id}`} className="font-semibold text-neutral-900 hover:text-blue-700 block truncate" title={d.name} data-testid={`doc-link-${d.id}`}>{d.name}</Link>
                        <div className="text-xs text-neutral-500 truncate" title={d.filename}>{d.filename}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3 font-mono">{d.period}</td>
                  <td className="px-5 py-3"><span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-xs font-medium">{d.authority}</span></td>
                  <td className="px-5 py-3 text-xs text-neutral-600 whitespace-nowrap" data-testid={`doc-emission-${d.id}`}>{fmtDate(d.emission_date)}</td>
                  <td className="px-5 py-3 text-xs text-neutral-600 whitespace-nowrap" data-testid={`doc-validity-${d.id}`}>{fmtDate(d.validity_date)}</td>
                  <td className={`px-5 py-3 text-xs whitespace-nowrap font-semibold ${d.status==="vencido" ? "text-rose-600" : d.status==="proximo" ? "text-amber-600" : "text-neutral-700"}`} data-testid={`doc-expiration-${d.id}`}>{fmtDate(d.expiration_date)}</td>
                  <td className="px-5 py-3"><span className="px-2 py-0.5 bg-rose-50 text-rose-700 rounded text-xs font-medium">{d.genre}</span></td>
                  <td className="px-5 py-3 whitespace-nowrap">
                    {d.status==="vigente" && <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded text-xs font-semibold border border-emerald-100">Vigente</span>}
                    {d.status==="proximo" && <span className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-xs font-semibold border border-amber-100">Próximo {d.days_to_expire}d</span>}
                    {d.status==="vencido" && <span className="px-2 py-0.5 bg-rose-50 text-rose-700 rounded text-xs font-semibold border border-rose-100">Vencido</span>}
                    {d.status==="pendiente" && <span className="px-2 py-0.5 bg-neutral-100 text-neutral-600 rounded text-xs font-semibold">Pendiente</span>}
                  </td>
                  <td className="px-5 py-3">
                    {d.approval_status==="aprobado" && <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded text-xs font-semibold border border-emerald-100" data-testid={`approval-${d.id}`}>Aprobado</span>}
                    {d.approval_status==="rechazado" && <span className="px-2 py-0.5 bg-rose-50 text-rose-700 rounded text-xs font-semibold border border-rose-100" data-testid={`approval-${d.id}`}>Rechazado</span>}
                    {(!d.approval_status || d.approval_status==="pendiente") && <span className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-xs font-semibold border border-amber-100" data-testid={`approval-${d.id}`}>Pendiente</span>}
                  </td>
                  <td className="px-5 py-3 text-neutral-500 text-xs whitespace-nowrap">{d.created_at?.slice(0,10)}</td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <button onClick={()=>setViewDoc(d)}
                       className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-700 mr-3 text-xs font-semibold"
                       title="Visualizar" data-testid={`preview-${d.id}`}>
                      <Eye size={16} /><span className="sr-only">Visualizar</span>
                    </button>
                    <a href={`/api/documents/file/${d.drive_file_id}`}
                       target="_blank" rel="noreferrer"
                       className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 mr-3 text-xs font-semibold"
                       title="Descargar" data-testid={`download-${d.id}`}>
                      <DownloadSimple size={16} /><span className="sr-only">Descargar</span>
                    </a>
                    {canEdit && (!d.approval_status || d.approval_status === "pendiente" || d.approval_status === "rechazado") && (
                      <button onClick={()=>setEditDoc(d)}
                         className="inline-flex items-center gap-1 text-amber-600 hover:text-amber-700 mr-3 text-xs font-semibold"
                         title="Editar" data-testid={`edit-${d.id}`}>
                        <PencilSimple size={16} /><span className="sr-only">Editar</span>
                      </button>
                    )}
                    {canDelete && (
                      <button onClick={()=>onDelete(d.id)} className="text-rose-500 hover:text-rose-600" data-testid={`delete-doc-${d.id}`}>
                        <Trash size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={canEdit ? 12 : 11} className="text-center py-12 text-neutral-400">No hay documentos que coincidan con los filtros.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {wizardOpen && <UploadWizard onClose={()=>setWizardOpen(false)} onSuccess={()=>{ fetchDocs(); setWizardOpen(false); }} />}
      {viewDoc && <DocumentViewerModal docId={viewDoc.id} fileId={viewDoc.drive_file_id} mimeType={viewDoc.mime_type} filename={viewDoc.filename} onClose={()=>setViewDoc(null)}/>}
      {editDoc && <DocumentEditModal document={editDoc} onClose={()=>setEditDoc(null)} onSuccess={()=>{ setEditDoc(null); fetchDocs(); }}/>}
    </div>
  );
}

function UploadWizard({ onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [period, setPeriod] = useState(new Date().getFullYear());
  const [authority, setAuthority] = useState("");
  const [genre, setGenre] = useState(GENRES[0]);
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [installations, setInstallations] = useState([]);
  const [obligations, setObligations] = useState([]);
  const [installationId, setInstallationId] = useState("");
  const [obligationId, setObligationId] = useState("");
  const [category, setCategory] = useState("");
  const [docType, setDocType] = useState("");
  const [emissionDate, setEmissionDate] = useState("");
  const [validityDate, setValidityDate] = useState("");
  const [expirationDate, setExpirationDate] = useState("");
  const [responsible, setResponsible] = useState("");
  const [observations, setObservations] = useState("");

  const authorities = useCatalog("autoridad");
  const categories = useCatalog("categoria");
  const docTypes = useCatalog("tipo_documental");

  useEffect(()=>{
    api.get("/installations").then(r=>setInstallations(r.data)).catch(()=>{});
    api.get("/obligations").then(r=>setObligations(r.data)).catch(()=>{});
  },[]);
  // initialize authority once catalog arrives
  useEffect(()=>{ if(!authority && authorities.length) setAuthority(authorities[0].name); }, [authorities, authority]);

  const filteredObls = obligations.filter(o => !installationId || o.installation_id === installationId || !o.installation_id);

  const onDrop = useCallback((accepted) => {
    if (accepted.length) {
      setFile(accepted[0]);
      if (!name) setName(accepted[0].name.replace(/\.[^.]+$/, ""));
    }
  }, [name]);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, multiple: false });

  const submit = async () => {
    if (!file || !name) { toast.error("Archivo y nombre obligatorios"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("name", name);
      fd.append("period", period); fd.append("authority", authority);
      fd.append("genre", genre); fd.append("description", description);
      fd.append("installation_id", installationId); fd.append("obligation_id", obligationId);
      fd.append("category", category); fd.append("doc_type", docType);
      fd.append("emission_date", emissionDate); fd.append("validity_date", validityDate);
      fd.append("expiration_date", expirationDate);
      fd.append("responsible", responsible); fd.append("observations", observations);
      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento subido correctamente");
      onSuccess();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al subir");
    } finally { setBusy(false); }
  };

  const STEPS = 5;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="upload-wizard-modal">
      <div className="bg-white rounded-md w-full max-w-2xl border border-neutral-200 shadow-xl anim-fade-up max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-neutral-200">
          <div>
            <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">Asistente · paso {step}/{STEPS}</div>
            <h2 className="font-display text-2xl font-bold tracking-tight mt-1">Nuevo documento</h2>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600" data-testid="wizard-close-btn"><X size={22} /></button>
        </div>
        <div className="px-5 pt-4">
          <div className="flex gap-2">
            {Array.from({length:STEPS}).map((_,i)=>(<div key={i} className={`flex-1 h-1.5 rounded-full ${i+1 <= step ? 'bg-blue-600' : 'bg-neutral-200'}`}></div>))}
          </div>
        </div>

        <div className="p-6">
          {step === 1 && (
            <div className="space-y-4">
              <h3 className="font-display text-lg font-bold">1. Instalación y obligación</h3>
              <div>
                <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Instalación</label>
                <select value={installationId} onChange={(e)=>setInstallationId(e.target.value)} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md bg-white" data-testid="wiz-installation">
                  <option value="">— Sin instalación —</option>
                  {installations.map(i=><option key={i.id} value={i.id}>{i.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Obligación</label>
                <select value={obligationId} onChange={(e)=>setObligationId(e.target.value)} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md bg-white" data-testid="wiz-obligation">
                  <option value="">— Sin obligación —</option>
                  {filteredObls.map(o=><option key={o.id} value={o.id}>{o.name} ({o.authority})</option>)}
                </select>
              </div>
            </div>
          )}
          {step === 2 && (
            <div className="space-y-4">
              <h3 className="font-display text-lg font-bold">2. Clasificación</h3>
              <div>
                <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Nombre del documento</label>
                <input value={name} onChange={(e)=>setName(e.target.value)} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none" data-testid="doc-name-input"/>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Período</label>
                  <select value={period} onChange={(e)=>setPeriod(e.target.value)} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md bg-white" data-testid="doc-period-select">
                    {YEARS.map(y => <option key={y} value={y}>{y}</option>)}</select></div>
                <div><label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Autoridad</label>
                  <select value={authority} onChange={(e)=>setAuthority(e.target.value)} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md bg-white" data-testid="doc-authority-select">
                    <option value="">Seleccionar...</option>
                    {authorities.map(a => <option key={a.id} value={a.name}>{a.name}</option>)}</select></div>
                <div><label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Género</label>
                  <select value={genre} onChange={(e)=>setGenre(e.target.value)} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md bg-white" data-testid="doc-genre-select">
                    {GENRES.map(g => <option key={g} value={g}>{g}</option>)}</select></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Categoría documental</label>
                  <select value={category} onChange={(e)=>setCategory(e.target.value)} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md bg-white" data-testid="wiz-category">
                    <option value="">Seleccionar...</option>
                    {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}</select></div>
                <div><label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Tipo documental</label>
                  <select value={docType} onChange={(e)=>setDocType(e.target.value)} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md bg-white" data-testid="wiz-doctype">
                    <option value="">Seleccionar...</option>
                    {docTypes.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}</select></div>
              </div>
            </div>
          )}
          {step === 3 && (
            <div className="space-y-4">
              <h3 className="font-display text-lg font-bold">3. Archivo</h3>
              <div {...getRootProps()} className={`border-dashed border-2 rounded-md p-12 text-center cursor-pointer transition-all ${isDragActive ? 'dropzone-active border-blue-500' : 'border-neutral-300 hover:border-blue-400 hover:bg-blue-50/30'}`} data-testid="dropzone">
                <input {...getInputProps()} data-testid="file-input"/>
                <UploadSimple size={48} className="mx-auto text-blue-600 mb-3" weight="duotone" />
                {file ? (
                  <div><div className="font-semibold text-neutral-900">{file.name}</div><div className="text-xs text-neutral-500 mt-1">{(file.size/1024).toFixed(1)} KB</div></div>
                ) : (<><p className="font-semibold text-neutral-900">Arrastra y suelta tu archivo aquí</p><p className="text-sm text-neutral-500 mt-1">o haz clic para buscar</p></>)}
              </div>
            </div>
          )}
          {step === 4 && (
            <div className="space-y-4">
              <h3 className="font-display text-lg font-bold">4. Fechas y responsable</h3>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Emisión</label>
                  <input type="date" value={emissionDate} onChange={(e)=>setEmissionDate(e.target.value)} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md" data-testid="wiz-emission"/></div>
                <div><label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Vigencia</label>
                  <input type="date" value={validityDate} onChange={(e)=>setValidityDate(e.target.value)} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md" data-testid="wiz-validity"/></div>
                <div><label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Vencimiento</label>
                  <input type="date" value={expirationDate} onChange={(e)=>setExpirationDate(e.target.value)} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md" data-testid="wiz-expiration"/></div>
              </div>
              <div><label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Responsable</label>
                <input value={responsible} onChange={(e)=>setResponsible(e.target.value)} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md" data-testid="wiz-responsible"/></div>
              <div><label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Observaciones</label>
                <textarea value={observations} onChange={(e)=>setObservations(e.target.value)} rows={2} className="w-full px-3 py-2 border border-neutral-200 rounded-md" data-testid="wiz-observations"/></div>
            </div>
          )}
          {step === 5 && (
            <div className="space-y-4">
              <h3 className="font-display text-lg font-bold">5. Confirmar y subir</h3>
              <div className="bg-neutral-50 border border-neutral-200 rounded-md p-5 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-neutral-500">Archivo:</span><span className="font-semibold">{file?.name||"—"}</span></div>
                <div className="flex justify-between"><span className="text-neutral-500">Nombre:</span><span className="font-semibold">{name}</span></div>
                <div className="flex justify-between"><span className="text-neutral-500">Instalación:</span><span className="font-semibold">{installations.find(i=>i.id===installationId)?.name||"—"}</span></div>
                <div className="flex justify-between"><span className="text-neutral-500">Obligación:</span><span className="font-semibold">{obligations.find(o=>o.id===obligationId)?.name||"—"}</span></div>
                <div className="flex justify-between"><span className="text-neutral-500">Autoridad:</span><span className="font-semibold">{authority}</span></div>
                <div className="flex justify-between"><span className="text-neutral-500">Vencimiento:</span><span className="font-semibold">{expirationDate||"—"}</span></div>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between p-5 border-t border-neutral-200 bg-neutral-50">
          <button onClick={()=>step>1 && setStep(step-1)} disabled={step===1} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-neutral-600 hover:text-neutral-900 disabled:opacity-40" data-testid="wizard-prev-btn">
            <ArrowLeft size={16} /> Anterior
          </button>
          {step < STEPS ? (
            <button onClick={()=>{ if (step===2 && !name) { toast.error("Ingresa un nombre"); return; } if (step===3 && !file) { toast.error("Selecciona un archivo"); return; } setStep(step+1); }}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md" data-testid="wizard-next-btn">
              Siguiente <ArrowRight size={16} />
            </button>
          ) : (
            <button onClick={submit} disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-md disabled:opacity-60" data-testid="wizard-submit-btn">
              {busy ? "Subiendo..." : <>Subir documento <CheckCircle size={16} /></>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
